import React from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface QuickDateButtonsProps {
  startDate: Date | null;
  onDatesSelected: (startDate: Date, endDate: Date) => void;
  disabled?: boolean;
}

const QuickDateButtons: React.FC<QuickDateButtonsProps> = ({
  startDate,
  onDatesSelected,
  disabled = false
}) => {
  const { t } = useTranslation();

  const calculateEndDate = (minutes: number): void => {
    if (!startDate) return;

    const endDate = new Date(startDate.getTime() + minutes * 60 * 1000);
    onDatesSelected(startDate, endDate);
  };

  const setHalfDayMorning = (): void => {
    if (!startDate) return;

    // Demi-journée matin: 8h00 → 12h00
    const start = new Date(startDate);
    start.setHours(8, 0, 0, 0);

    const end = new Date(start);
    end.setHours(12, 0, 0, 0);

    onDatesSelected(start, end);
  };

  const setHalfDayAfternoon = (): void => {
    if (!startDate) return;

    // Demi-journée après-midi: 13h00 → 17h00
    const start = new Date(startDate);
    start.setHours(13, 0, 0, 0);

    const end = new Date(start);
    end.setHours(17, 0, 0, 0);

    onDatesSelected(start, end);
  };

  const setFullDay = (): void => {
    if (!startDate) return;

    // Journée complète: 8h00 → 17h00
    const start = new Date(startDate);
    start.setHours(8, 0, 0, 0);

    const end = new Date(start);
    end.setHours(17, 0, 0, 0);

    onDatesSelected(start, end);
  };

  const setTwoDays = (): void => {
    if (!startDate) return;

    // 2 jours: 8h00 jour 1 → 17h00 jour 2
    const start = new Date(startDate);
    start.setHours(8, 0, 0, 0);

    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    end.setHours(17, 0, 0, 0);

    onDatesSelected(start, end);
  };

  const buttons = [
    {
      label: t('reservation:quickDates.twoHours', '+ 2 h'),
      onClick: () => calculateEndDate(120),
    },
    {
      label: t('reservation:quickDates.halfDayMorning', '½ journée matin'),
      onClick: setHalfDayMorning,
    },
    {
      label: t('reservation:quickDates.halfDayAfternoon', '½ journée après-midi'),
      onClick: setHalfDayAfternoon,
    },
    {
      label: t('reservation:quickDates.fullDay', 'Journée'),
      onClick: setFullDay,
    },
    {
      label: t('reservation:quickDates.twoDays', '2 jours'),
      onClick: setTwoDays,
    },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
      {buttons.map((button, index) => (
        <Button
          key={index}
          variant="outlined"
          size="small"
          onClick={button.onClick}
          disabled={disabled || !startDate}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
          }}
        >
          {button.label}
        </Button>
      ))}
    </Box>
  );
};

export default QuickDateButtons;
